class ExperimentalStickyBuyBar extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.select = this.querySelector('[data-sticky-variant-select]');
    this.variantButtons = [...this.querySelectorAll('[data-sticky-variant-button]')];
    this.variantTrigger = this.querySelector('[data-sticky-variant-trigger]');
    this.variantLabel = this.querySelector('[data-sticky-variant-label]');
    this.variantSheet = this.querySelector('[data-sticky-variant-sheet]');
    this.variantSheetClose = this.querySelector('[data-sticky-variant-close]');
    this.variantSheetButtons = [...this.querySelectorAll('[data-sticky-sheet-variant-button]')];
    this.button = this.querySelector('[data-sticky-atc]');
    this.price = this.querySelector('[data-sticky-price]');
    this.trigger = document.querySelector('.product-information .buy-buttons-block');
    this.mainButton = this.trigger?.querySelector('[ref="addToCartButton"]');
    this.header = document.querySelector('header-component, .header-section');
    this.triggerSearchAttempts = 0;

    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleOpenVariantSheet = this.handleOpenVariantSheet.bind(this);
    this.handleCloseVariantSheet = this.handleCloseVariantSheet.bind(this);
    this.handleVariantSheetCancel = this.handleVariantSheetCancel.bind(this);
    this.handleVariantSheetBackdrop = this.handleVariantSheetBackdrop.bind(this);
    this.handleVariantSheetSelection = this.handleVariantSheetSelection.bind(this);

    this.select?.addEventListener('change', this.handleVariantChange);
    this.variantButtons.forEach((button) => button.addEventListener('click', this.handleVariantChange));
    this.variantTrigger?.addEventListener('click', this.handleOpenVariantSheet);
    this.variantSheetClose?.addEventListener('click', this.handleCloseVariantSheet);
    this.variantSheet?.addEventListener('cancel', this.handleVariantSheetCancel);
    this.variantSheet?.addEventListener('click', this.handleVariantSheetBackdrop);
    this.variantSheetButtons.forEach((button) => button.addEventListener('click', this.handleVariantSheetSelection));
    this.button?.addEventListener('click', this.handleAddToCart);
    document.addEventListener('variant:update', this.handleVariantUpdate);
    window.addEventListener('resize', this.handleResize, { passive: true });

    if (this.variantTrigger && typeof this.variantSheet?.showModal === 'function') {
      this.setAttribute('data-variant-sheet-enhanced', '');
    }

    this.setupObserver();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.select?.removeEventListener('change', this.handleVariantChange);
    this.variantButtons.forEach((button) => button.removeEventListener('click', this.handleVariantChange));
    this.variantTrigger?.removeEventListener('click', this.handleOpenVariantSheet);
    this.variantSheetClose?.removeEventListener('click', this.handleCloseVariantSheet);
    this.variantSheet?.removeEventListener('cancel', this.handleVariantSheetCancel);
    this.variantSheet?.removeEventListener('click', this.handleVariantSheetBackdrop);
    this.variantSheetButtons.forEach((button) => button.removeEventListener('click', this.handleVariantSheetSelection));
    this.button?.removeEventListener('click', this.handleAddToCart);
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    window.removeEventListener('resize', this.handleResize);
    window.clearTimeout(this.variantSheetCloseTimer);
    window.clearTimeout(this.triggerSearchTimer);
    document.documentElement.classList.remove('sticky-variant-sheet-open');
    this.variantSheet?.classList.remove('is-open');
    if (this.variantSheet?.open) this.variantSheet.close();
    this.removeAttribute('data-visible');
    this.removeAttribute('data-variant-sheet-enhanced');
    this.setAttribute('aria-hidden', 'true');
    this.isInitialized = false;
  }

  getHeaderOffset() {
    return this.header?.getBoundingClientRect().height || 0;
  }

  setupObserver() {
    this.observer?.disconnect();
    window.clearTimeout(this.triggerSearchTimer);
    this.trigger = document.querySelector('.product-information .buy-buttons-block');
    this.mainButton = this.trigger?.querySelector('[ref="addToCartButton"]');

    if (!this.trigger) {
      if (this.triggerSearchAttempts < 20) {
        this.triggerSearchAttempts += 1;
        this.triggerSearchTimer = window.setTimeout(() => this.setupObserver(), 100);
      }
      return;
    }

    this.triggerSearchAttempts = 0;

    const headerOffset = this.getHeaderOffset();
    this.observer = new IntersectionObserver(
      ([entry]) => {
        const triggerIsAboveViewport = entry.boundingClientRect.bottom <= headerOffset;
        this.toggleAttribute('data-visible', !entry.isIntersecting && triggerIsAboveViewport);
        this.setAttribute('aria-hidden', String(entry.isIntersecting || !triggerIsAboveViewport));
      },
      { rootMargin: `-${headerOffset}px 0px 0px 0px`, threshold: 0 }
    );
    this.observer.observe(this.trigger);
  }

  handleResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => this.setupObserver(), 150);
  }

  handleVariantChange(event) {
    const target = event.currentTarget;
    const selectedUrl = target instanceof HTMLSelectElement
      ? target.selectedOptions?.[0]?.value
      : target?.dataset.variantUrl;
    this.navigateToVariant(selectedUrl);
  }

  navigateToVariant(selectedUrl) {
    if (!selectedUrl) return;
    const nextUrl = new URL(selectedUrl, window.location.origin);
    const currentView = new URL(window.location.href).searchParams.get('view');
    if (currentView) nextUrl.searchParams.set('view', currentView);
    window.location.assign(nextUrl.toString());
  }

  handleOpenVariantSheet() {
    if (!this.variantSheet || this.variantSheet.open) return;

    window.clearTimeout(this.variantSheetCloseTimer);
    this.variantSheet.showModal();
    this.variantTrigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('sticky-variant-sheet-open');

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.variantSheet?.classList.add('is-open'));
    });
  }

  handleCloseVariantSheet() {
    this.closeVariantSheet();
  }

  closeVariantSheet({ restoreFocus = true, afterClose } = {}) {
    if (!this.variantSheet?.open) {
      afterClose?.();
      return;
    }

    this.variantSheet.classList.remove('is-open');
    this.variantTrigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('sticky-variant-sheet-open');
    window.clearTimeout(this.variantSheetCloseTimer);

    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
    this.variantSheetCloseTimer = window.setTimeout(() => {
      this.variantSheet?.close();
      if (restoreFocus) this.variantTrigger?.focus();
      afterClose?.();
    }, closeDelay);
  }

  handleVariantSheetCancel(event) {
    event.preventDefault();
    this.closeVariantSheet();
  }

  handleVariantSheetBackdrop(event) {
    if (event.target === this.variantSheet) this.closeVariantSheet();
  }

  handleVariantSheetSelection(event) {
    const selectedUrl = event.currentTarget?.dataset.variantUrl;
    if (!selectedUrl) return;

    this.closeVariantSheet({
      restoreFocus: false,
      afterClose: () => this.navigateToVariant(selectedUrl),
    });
  }

  handleAddToCart() {
    this.mainButton = document.querySelector('.product-information .buy-buttons-block [ref="addToCartButton"]');
    this.mainButton?.click();
  }

  handleVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    const matchingOption = this.select?.querySelector(`[data-variant-id="${variant.id}"]`);
    if (matchingOption) this.select.value = matchingOption.value;

    this.variantButtons.forEach((button) => {
      const active = button.dataset.variantId === String(variant.id);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    this.variantSheetButtons.forEach((button) => {
      const active = button.dataset.variantId === String(variant.id);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      if (active && this.variantLabel) this.variantLabel.textContent = button.dataset.variantLabel;
    });

    const source = event.detail?.data?.html;
    const newBar = source?.querySelector('experimental-sticky-buy-bar');
    if (this.price && newBar) {
      const newPrice = newBar.querySelector('[data-sticky-price]');
      if (newPrice) this.price.innerHTML = newPrice.innerHTML;
    }

    if (this.button && newBar) {
      const canAdd = newBar.dataset.canAdd === 'true';
      this.button.disabled = !canAdd;
      this.button.textContent = canAdd ? this.dataset.addLabel : this.dataset.soldOutLabel;
    }
  }
}

if (!customElements.get('experimental-sticky-buy-bar')) {
  customElements.define('experimental-sticky-buy-bar', ExperimentalStickyBuyBar);
}
